'use client';

import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { useRouter } from '@/lib/navigation';
import { useAuth } from '@/context/AuthContext';

const GameContext = createContext(null);

const SOCKET_URL = process.env.NEXT_PUBLIC_SOCKET_URL || 'http://localhost:4000';

export function GameProvider({ children }) {
  const router = useRouter();
  const { user, refreshUser } = useAuth();
  const refreshUserRef = useRef(refreshUser);
  useEffect(() => { refreshUserRef.current = refreshUser; }, [refreshUser]);
  const leftGameCodeRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [gameState, setGameState] = useState(null);
  const [gameId, setGameId] = useState(null);
  const [gameCode, setGameCode] = useState(null);
  const [lobbyPlayers, setLobbyPlayers] = useState([]);
  const [isHost, setIsHost] = useState(false);
  const [error, setError] = useState(null);
  const [heroSelectionActive, setHeroSelectionActive] = useState(false);
  const [sinAbsorbedEvent, setSinAbsorbedEvent] = useState(null);
  const [pendingEvent, setPendingEvent] = useState(null);
  const [passiveAbilityRequest, setPassiveAbilityRequest] = useState(null);
  const [heroAbilitySelection, setHeroAbilitySelection] = useState(null);
  const [heroAbilityModal, setHeroAbilityModal] = useState(null);
  const [gameEndData, setGameEndData] = useState(null);
  const [eventEffectsAnimation, setEventEffectsAnimation] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);
  const [onlinePlayers, setOnlinePlayers] = useState([]);
  const [openLobbies, setOpenLobbies] = useState([]);
  const [notification, setNotification] = useState(null);
  const [contractCheckResult, setContractCheckResult] = useState(null);
  const [fulfilledContractIds, setFulfilledContractIds] = useState([]);
  const [victorySplashAckCount, setVictorySplashAckCount] = useState(0);
  const [victorySplashAckTotal, setVictorySplashAckTotal] = useState(1);
  const [victorySplashAllDone, setVictorySplashAllDone] = useState(false);
  const [alarmBreach, setAlarmBreach] = useState(null);
  const [castleMarchAnimating, setCastleMarchAnimating] = useState(false);
  const [dailyQuests, setDailyQuests] = useState([]);
  const [isLobbyViewActive, setIsLobbyViewActive] = useState(false);
  const [lobbyViewRequest, setLobbyViewRequest] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [spectatorCityZoneOpen, setSpectatorCityZoneOpen] = useState(false);
  const [spectatorCityZoneUser, setSpectatorCityZoneUser] = useState(null);
  const [activeGames, setActiveGames] = useState([]);
  const [isSpectator, setIsSpectator] = useState(false);
  const [spectatorCount, setSpectatorCount] = useState(0);
  const [pendingAchievements, setPendingAchievements] = useState([]);
  const [backgroundProgress, setBackgroundProgress] = useState(null);
  const [pendingBackgroundUnlocks, setPendingBackgroundUnlocks] = useState([]);
  const [avatarFrameProgress, setAvatarFrameProgress] = useState(null);
  const [pendingAvatarFrameUnlocks, setPendingAvatarFrameUnlocks] = useState([]);
  // Tryb Pojedynek z Legendą
  const [comboChainEvent, setComboChainEvent] = useState(null);
  const [comboResolveEvent, setComboResolveEvent] = useState(null);
  const [comboAttackEvent, setComboAttackEvent] = useState(null);
  const [comboWinner, setComboWinner] = useState(null);
  const [comboHealEvent, setComboHealEvent] = useState(null);
  // System Wyzwań
  const [challengeShare, setChallengeShare] = useState(null);
  const [challengeStartData, setChallengeStartData] = useState(null);
  const [challengeError, setChallengeError] = useState(null);
  const [challengeInvitesSent, setChallengeInvitesSent] = useState(null);

  const requestLobbyView = useCallback(() => {
    console.log('[GameContext] requestLobbyView triggered');
    setLobbyViewRequest(prev => prev + 1);
  }, []);

  const dismissAchievement = useCallback((queueId) => {
    setPendingAchievements(prev => prev.filter(a => a._queueId !== queueId));
  }, []);

  const dismissBackgroundUnlock = useCallback((queueId) => {
    setPendingBackgroundUnlocks(prev => prev.filter(b => b._queueId !== queueId));
  }, []);

  const clearBackgroundProgress = useCallback(() => {
    setBackgroundProgress(null);
  }, []);

  const dismissAvatarFrameUnlock = useCallback((queueId) => {
    setPendingAvatarFrameUnlocks(prev => prev.filter(f => f._queueId !== queueId));
  }, []);

  // Liczenie unseen unlocks per-kategoria (tytuły / tła / ramki) — używane przez
  // kropkę przy avatarze w menu głównym i tabie "Osiągnięcia" w profilu.
  const globalSeenMs = user?.lastSeenAchievementsAt ? new Date(user.lastSeenAchievementsAt).getTime() : 0;
  const countUnseen = (history, lastSeenField) => {
    if (!history?.length) return 0;
    const lastSeenValue = user?.[lastSeenField];
    const seenMs = lastSeenValue
      ? new Date(lastSeenValue).getTime()
      : globalSeenMs;
    return history.reduce((n, h) => {
      const ts = h?.unlockedAt ? new Date(h.unlockedAt).getTime() : 0;
      return ts > seenMs ? n + 1 : n;
    }, 0);
  };
  const unseenTitlesCount = countUnseen(user?.unlockedAchievements, 'lastSeenTitlesAt');
  const unseenBackgroundsCount = countUnseen(user?.unlockedBackgroundsHistory, 'lastSeenBackgroundsAt');
  const unseenFramesCount = countUnseen(user?.unlockedAvatarFramesHistory, 'lastSeenAvatarFramesAt');
  const persistedUnseenCount = unseenTitlesCount + unseenBackgroundsCount + unseenFramesCount;
  const unseenAchievementsCount = Math.max(persistedUnseenCount, pendingAchievements.length);
  const hasUnseenAchievements = unseenAchievementsCount > 0;

  const clearAvatarFrameProgress = useCallback(() => {
    setAvatarFrameProgress(null);
  }, []);

  // Synchronize pendingEvent from gameState
  useEffect(() => {
    if (gameState?.pendingEvent && !pendingEvent) {
      console.log('[SYNC] Detected pending event in gameState:', gameState.pendingEvent);
      const currentUserId = localStorage.getItem('userId');
      const acknowledgedBy = gameState.pendingEvent.acknowledgedBy || [];
      setPendingEvent({
        eventCard: gameState.pendingEvent.eventCard,
        eventCardId: gameState.pendingEvent.cardId,
        triggeringPlayer: {
          userId: gameState.pendingEvent.triggeringPlayerId,
          username: gameState.pendingEvent.triggeringPlayerName
        },
        effects: gameState.pendingEvent.effects || [],
        source: gameState.pendingEvent.source || null,
        pathType: gameState.pendingEvent.pathType || null,
        cyclePhase: gameState.pendingEvent.cyclePhase || null,
        canAcknowledge: !acknowledgedBy.includes(currentUserId),
        acknowledgedBy,
        acknowledgedCount: acknowledgedBy.length,
        totalRequired: gameState.players?.filter(p => !p.isBot).length || 1
      });
    } else if (!gameState?.pendingEvent && pendingEvent) {
      setPendingEvent(null);
    }
  }, [gameState?.pendingEvent, pendingEvent]);

  // Initialize socket connection
  useEffect(() => {
    const socketInstance = io(SOCKET_URL);

    socketInstance.on('connect', () => {
      setIsConnected(true);
      // Auto-rejoin as spectator on page refresh
      try {
        const savedSpectateCode = localStorage.getItem('spectatorGameCode');
        if (savedSpectateCode) {
          console.log('[GameContext] Rejoining spectate session:', savedSpectateCode);
          socketInstance.emit('game:spectate', { gameCode: savedSpectateCode });
        }
      } catch {}
    });

    socketInstance.on('disconnect', () => {
      setIsConnected(false);
      setOnlineCount(0);
      console.log('Socket disconnected');
    });

    socketInstance.on('online_count', (count) => {
      setOnlineCount(count);
    });

    socketInstance.on('social:online_players', (players) => {
      setOnlinePlayers(players);
    });

    socketInstance.on('game:open_lobbies', (lobbies) => {
      setOpenLobbies(lobbies);
    });

    socketInstance.on('game:player_left', (data) => {
      if (data.userId) {
        setLobbyPlayers(prev => prev.filter(p => p.userId !== data.userId));
      }
    });

    socketInstance.on('game:player_kicked', (data) => {
      console.log('Kicked from lobby:', data);
      setNotification({ type: 'warning', message: data.reason || 'Zostałeś wyrzucony z lobby' });
      setTimeout(() => setNotification(null), 4000);
      // Reset game state
      setGameState(null);
      setGameId(null);
      setGameCode(null);
      setLobbyPlayers([]);
      setIsHost(false);
      setHeroSelectionActive(false);
    });

    socketInstance.on('game:active_games', (data) => {
      setActiveGames(data || []);
    });

    socketInstance.on('game:spectate_joined', (data) => {
      console.log('Spectate joined:', data);
      if (data.success) {
        setGameState(data.game);
        setIsSpectator(true);
        setSpectatorCount(data.spectatorCount || 0);
        if (data.game?.code) {
          setGameCode(data.game.code);
          // Persist spectator state for page refresh
          try { localStorage.setItem('spectatorGameCode', data.game.code); } catch {}
        }
      }
    });

    socketInstance.on('game:spectator_count_update', (data) => {
      setSpectatorCount(data.count || 0);
    });

    socketInstance.on('game:created', (data) => {
      console.log('Game created:', data);
      if (data.success) {
        setGameId(data.gameId);
        setGameCode(data.code);
        setGameState(data.game);
        setIsHost(true);
        if (data.game?.players) {
          setLobbyPlayers(data.game.players);
        }
      }
    });

    socketInstance.on('game:joined', (data) => {
      console.log('Joined game:', data);
      if (data.success) {
        setGameId(data.gameId);
        setGameState(data.game);
        if (data.game?.code) {
          setGameCode(data.game.code);
        }
        const currentUserId = localStorage.getItem('userId');
        if (data.game?.hostId && currentUserId) {
          setIsHost(data.game.hostId === currentUserId);
        }
        if (data.game?.players) {
          setLobbyPlayers(data.game.players);
        }
      }
    });

    socketInstance.on('game:player_joined', (data) => {
      console.log('Player joined:', data);
      if (data.players) {
        setLobbyPlayers(data.players);
      } else {
        setLobbyPlayers(prev => [...prev, { username: data.username, userId: data.userId }]);
      }
    });

    socketInstance.on('game:bot_added', (data) => {
      if (data.players) setLobbyPlayers(data.players);
      if (data.game) setGameState(data.game);
    });

    socketInstance.on('game:bot_removed', (data) => {
      if (data.players) setLobbyPlayers(data.players);
      if (data.game) setGameState(data.game);
    });

    socketInstance.on('game:started', (data) => {
      console.log('Game started:', data);
      if (data.success) {
        setGameState(data.game);
      }
    });

    const applyStateUpdate = (data) => {
      if (data?.game?.code && data.game.code === leftGameCodeRef.current) return;
      setGameState(data.game);
      setHeroAbilitySelection(null);
      if (data.game?.players) {
        setLobbyPlayers(data.game.players);
      }
    };
    // Koalescencja stanów: tura bota potrafi przysłać kilka pełnych stanów na sekundę,
    // a każdy oznacza pełne przerysowanie stołu. Stany z tur botów są dla gracza tylko
    // do oglądania, więc na słabych urządzeniach (data-perf="low") i na TV stosujemy
    // „najnowszy wygrywa" co najwyżej co minMs. Stan z MOJĄ turą zawsze trafia
    // natychmiast — własna responsywność stołu jest ważniejsza niż oszczędność klatek.
    const TV_STATE_MIN_MS = 500; // profil z TV: pełne przerysowanie stołu ~70 ms, 3/s zjadało ~22% wątku
    const LOWPERF_STATE_MIN_MS = 350;
    let coalescePending = null;
    let coalesceTimer = null;
    let coalesceLastApplied = 0;
    const coalesced = (data, minMs) => {
      coalescePending = data;
      if (coalesceTimer) return;
      const wait = Math.max(0, minMs - (Date.now() - coalesceLastApplied));
      coalesceTimer = setTimeout(() => {
        coalesceTimer = null;
        coalesceLastApplied = Date.now();
        const next = coalescePending;
        coalescePending = null;
        if (next) applyStateUpdate(next);
      }, wait);
    };
    socketInstance.on('game:state_update', (data) => {
      if (data?.game?.isTvView) return coalesced(data, TV_STATE_MIN_MS);
      const myId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      const isMyTurnState = data?.game?.currentPlayer?.userId
        && String(data.game.currentPlayer.userId) === String(myId);
      const lowPerf = typeof document !== 'undefined'
        && document.documentElement.dataset.perf === 'low';
      if (lowPerf && !isMyTurnState) return coalesced(data, LOWPERF_STATE_MIN_MS);
      // Świeży, natychmiastowy stan — jeśli w kolejce czeka zbuforowany update,
      // wyczyść go: ten nowszy stan zawiera wszystko, co pending.
      if (coalesceTimer) {
        clearTimeout(coalesceTimer);
        coalesceTimer = null;
        coalescePending = null;
      }
      applyStateUpdate(data);
    });

    socketInstance.on('game:turn_started', (data) => console.log('Turn started:', data));
    socketInstance.on('game:turn_ended', (data) => {
      console.log('Turn ended:', data);
      if (data.summary?.isLastRound && !gameState?.isLastRound) {
        setNotification({
          type: 'success',
          message: 'Runda stołu dobiega końca! To ostatnia szansa na zdobycie Sławy.'
        });
        setTimeout(() => setNotification(null), 6000);
      }
    });
    socketInstance.on('game:card_played', (data) => console.log('Card played:', data));
    socketInstance.on('game:card_purchased', (data) => console.log('Card purchased:', data));
    socketInstance.on('game:challenge_completed', (data) => console.log('Challenge completed:', data));
    socketInstance.on('game:challenge_result', (data) => console.log('Challenge result:', data));

    socketInstance.on('game:ended', (data) => {
      console.log('Game ended:', data);
      if (data?.finalState?.code && data.finalState.code === leftGameCodeRef.current) return;
      // Guard: if user already left (gameState=null), don't restore a partial state
      setGameState(prev => {
        if (!prev) return prev;
        return { ...prev, status: 'finished', winner: data.winner };
      });
      setGameEndData({
        winner: data.winner,
        isTie: data.isTie || false,
        endGameReason: data.endGameReason,
        finalState: data.finalState,
        statistics: data.statistics,
        timeline: data.timeline,
        eventLog: data.eventLog,
        gameDuration: data.gameDuration,
        gameStartedAt: data.gameStartedAt,
        gameEndedAt: data.gameEndedAt,
        finalScoreBreakdowns: data.finalScoreBreakdowns || null,
      });
      // Clear spectator persistence when game ends
      try { localStorage.removeItem('spectatorGameCode'); } catch {}
    });

    socketInstance.on('game:player_disconnected', (data) => {
      console.log('Player disconnected:', data);
      if (data?.username) {
        setNotification({ type: 'warning', message: `${data.username} stracił połączenie. Czekamy na powrót...` });
        setTimeout(() => setNotification(null), 4000);
      }
    });

    socketInstance.on('game:player_reconnected', (data) => {
      console.log('Player reconnected:', data);
      if (data?.username) {
        setNotification({ type: 'success', message: `${data.username} wrócił do gry.` });
        setTimeout(() => setNotification(null), 3000);
      }
    });

    socketInstance.on('game:chat_message', (data) => {
      setChatMessages(prev => [...prev.slice(-99), data]);
    });

    socketInstance.on('game:notification', (data) => {
      setNotification(data);
      setTimeout(() => setNotification(null), 3000);
    });

    socketInstance.on('game:hero_selection_started', (data) => {
      console.log('Hero selection started:', data);
      if (data.game) {
        setGameState(data.game);
        const currentUserId = localStorage.getItem('userId');
        if (data.game?.hostId && currentUserId) {
          setIsHost(data.game.hostId === currentUserId);
        }
        if (data.game.players) {
          setLobbyPlayers(data.game.players);
        }
      }
      setHeroSelectionActive(true);
    });

    socketInstance.on('hero:selected', (data) => {
      if (data.gameState) {
        setGameState(data.gameState);
        if (data.gameState.players) setLobbyPlayers(data.gameState.players);
      }
    });

    socketInstance.on('hero:all_selected', (data) => {
      if (data.gameState) {
        setGameState(data.gameState);
        if (data.gameState.players) setLobbyPlayers(data.gameState.players);
      }
    });

    socketInstance.on('game:rejoined', (data) => {
      if (data?.game?.code && data.game.code === leftGameCodeRef.current) return;
      if (data.success) {
        setGameId(data.gameId);
        setGameState(data.game);
        if (data.game?.code) setGameCode(data.game.code);
        const currentUserId = localStorage.getItem('userId');
        if (data.game?.hostId && currentUserId) {
          setIsHost(data.game.hostId === currentUserId);
        }
        if (data.game?.players) setLobbyPlayers(data.game.players);
        if (data.heroSelectionPhase) setHeroSelectionActive(true);
        if (data.game?.status === 'finished' && data.gameEndData) {
          setGameEndData(data.gameEndData);
        }
      }
    });

    socketInstance.on('game:redirect_to_new_lobby', (data) => {
      const currentUserId = localStorage.getItem('userId');
      if (data.players.includes(currentUserId)) {
        setTimeout(() => {
          if (data.newCode) {
            router.push(`/?join=${data.newCode}`);
          } else {
            // Redirect to menu (e.g. lobby closed)
            router.push('/');
            // Also reset local state to ensure clean slate
            setGameState(null);
            setGameId(null);
            setGameCode(null);
            setLobbyPlayers([]);
            setIsHost(false);
            setHeroSelectionActive(false);
          }
        }, 1500);
      }
    });

    socketInstance.on('game:event_revealed', (data) => {
      const currentUserId = localStorage.getItem('userId');
      setPendingEvent({
        eventCard: data.eventCard,
        eventCardId: data.eventCardId,
        triggeringPlayer: data.triggeringPlayer,
        effects: data.effects,
        canAcknowledge: true,
        acknowledgedBy: [],
        acknowledgedCount: 0,
        totalRequired: data.totalRequired || 1,
        source: data.source || null,
        pathType: data.pathType || null,
        cyclePhase: data.cyclePhase || null
      });
      // Ustaw też gameState.pendingEvent optymistycznie, żeby sync-useEffect nie
      // wyczyścił właśnie ustawionego eventu, gdy odpowiadający broadcast stanu gry
      // jeszcze nie dotarł (wyścig — gubił modal eventu zmierzchu w Cyklu Dnia i Nocy).
      setGameState(prev => prev ? {
        ...prev,
        pendingEvent: {
          cardId: data.eventCardId,
          eventCard: data.eventCard,
          triggeringPlayerId: data.triggeringPlayer?.userId,
          triggeringPlayerName: data.triggeringPlayer?.username,
          effects: data.effects || [],
          acknowledged: false,
          acknowledgedBy: [],
          source: data.source || null,
          pathType: data.pathType || null,
          cyclePhase: data.cyclePhase || null
        }
      } : prev);
    });

    socketInstance.on('game:event_acknowledge_update', (data) => {
      const currentUserId = localStorage.getItem('userId');
      setPendingEvent(prev => prev ? {
        ...prev,
        canAcknowledge: !data.acknowledgedBy.includes(currentUserId),
        acknowledgedBy: data.acknowledgedBy,
        acknowledgedCount: data.acknowledgedCount,
        totalRequired: data.totalRequired
      } : prev);
    });

    socketInstance.on('game:event_acknowledged', (data) => {
      if (data.effects && data.effects.length > 0) {
        setEventEffectsAnimation({ effects: data.effects, visible: true });
      }
      setPendingEvent(null);
      setGameState(prev => prev ? { ...prev, pendingEvent: null } : prev);
    });

    socketInstance.on('game:show_passive_modal', (data) => {
      setPassiveAbilityRequest(data);
      setHeroAbilityModal({
        mode: 'passive',
        ability: data.ability,
        triggerContext: data.triggerContext
      });
    });

    socketInstance.on('game:passive_resolved', () => {
      setHeroAbilityModal(null);
      setPassiveAbilityRequest(null);
    });

    socketInstance.on('game:passive_declined', () => {
      setHeroAbilityModal(null);
      setPassiveAbilityRequest(null);
    });

    socketInstance.on('game:sin_absorbed', (data) => {
      setSinAbsorbedEvent(data);
    });

    socketInstance.on('game:spore_gained', (data) => {
      // Toast handled in GameView via gameState update
      console.log(`[SPORE] Gained spore token (${data.total}/4)`);
    });

    socketInstance.on('game:fury_gained', (data) => {
      // Horda Pustkowi "Szał Krwi" — licznik aktualizuje się przez gameState
      console.log(`[FURY] Gained fury token (total: ${data.total})`);
    });

    socketInstance.on('game:spore_placed', (data) => {
      console.log(`[SPORE] Placed spore on ${data.cardId} in ${data.zone} (count: ${data.count})`);
    });

    socketInstance.on('game:passive_needs_selection', (data) => {
      setHeroAbilityModal(prev => {
        if (!prev) {
          return {
            mode: 'passive',
            ability: { name: data.ability },
            selectionType: data.needSelection,
            selectionCount: data.count || 1,
            selectionOptions: data.options || []
          };
        }
        return {
          ...prev,
          selectionType: data.needSelection,
          selectionCount: data.count || 1,
          selectionOptions: data.options || prev.selectionOptions || []
        };
      });
    });

    socketInstance.on('game:scry_result', (data) => {
      setHeroAbilityModal({
        mode: 'scry',
        ability: { name: 'Podgląd karty', description: 'Możesz dobrać tę kartę lub zostawić ją na wierzchu talii' },
        scryCard: data.card,
        scryCardId: data.cardId,
        canDraw: data.canDraw
      });
    });

    socketInstance.on('game:hero_ability_needs_selection', (data) => {
      setHeroAbilitySelection(data);
    });

    socketInstance.on('game:show_active_ability_optional', (data) => {
      setHeroAbilityModal({
        mode: 'active_optional',
        ability: data.ability,
        hero: data.hero,
        triggerContext: data.context
      });
    });

    socketInstance.on('game:auto_active_resolved', () => setHeroAbilityModal(null));
    socketInstance.on('game:auto_active_declined', () => setHeroAbilityModal(null));

    socketInstance.on('quest:daily_list', (quests) => setDailyQuests(quests));
    socketInstance.on('quest:reward_claimed', (data) => {
      setNotification({ type: 'success', message: `Odebrano nagrodę! +${data.fameGained} Sławy` });
      setTimeout(() => setNotification(null), 3000);
    });

    socketInstance.on('game:contract_conditions_met', (data) => {
      console.log('[GameContext] contract_conditions_met:', data);
      setContractCheckResult({ success: true, ...data });
    });
    socketInstance.on('game:contract_conditions_failed', (data) => {
      console.log('[GameContext] contract_conditions_failed:', data);
      setContractCheckResult({ success: false, ...data });
    });
    socketInstance.on('game:fulfilled_contracts', (data) => {
      setFulfilledContractIds(data.fulfilledContractIds || []);
    });
    socketInstance.on('game:victory_splash_acknowledge_update', (data) => {
      setVictorySplashAckCount(data.count);
      setVictorySplashAckTotal(data.total);
    });
    socketInstance.on('game:victory_splash_all_done', () => {
      setVictorySplashAllDone(true);
    });
    socketInstance.on('game:contract_completed', (data) => {
      setNotification({ type: 'success', message: `Kontrakt sfinalizowany: ${data.contractName}` });
      setTimeout(() => setNotification(null), 3000);
      setContractCheckResult(null);
      setFulfilledContractIds([]);
    });

    // Castle Defense: alarm breach
    socketInstance.on('game:alarm_breach', (data) => {
      console.log('[GameContext] alarm_breach:', data);
      setAlarmBreach(data);
    });
    socketInstance.on('game:alarm_breach_acknowledged', () => {
      setAlarmBreach(null);
    });

    socketInstance.on('game:castle_march', () => {
      setCastleMarchAnimating(true);
      setTimeout(() => setCastleMarchAnimating(false), 2500);
    });

    socketInstance.on('game:castle_repaired', (data) => {
      console.log('[GameContext] castle_repaired:', data);
      setNotification({
        type: 'success',
        message: `${data.playerName}: +${data.healAmount} HP zamku${data.wasFree ? ' (darmowa naprawa)' : ''}`
      });
      setTimeout(() => setNotification(null), 2500);
    });

    socketInstance.on('game:castle_upgraded', (data) => {
      console.log('[GameContext] castle_upgraded:', data);
      setNotification({
        type: 'success',
        message: `${data.playerName}: zamek ulepszony do poziomu ${data.newLevel}!`
      });
      setTimeout(() => setNotification(null), 3500);
    });

    socketInstance.on('game:error', (data) => {
      console.error('Game error:', data?.error || JSON.stringify(data));
      const msg = data.error || data.message;
      setError(msg);
      setTimeout(() => setError(null), 5000);
      setNotification({ type: 'warning', message: msg });
      setTimeout(() => setNotification(null), 4000);
      // Clear spectator persistence if spectate rejoin failed
      try { localStorage.removeItem('spectatorGameCode'); } catch {}
    });

    // ── System Wyzwań ──
    socketInstance.on('challenge:created', (data) => {
      console.log('Challenge created:', data);
      if (data.success) {
        setChallengeShare({ code: data.code, challenge: data.challenge });
      }
    });

    socketInstance.on('challenge:started', (data) => {
      console.log('Challenge started:', data);
      if (data.success) {
        setGameId(data.gameId);
        setGameCode(data.code);
        setGameState(data.game);
        setChallengeStartData(data);
      }
    });

    socketInstance.on('challenge:invites_sent', (data) => {
      setChallengeInvitesSent(data.sent);
    });

    socketInstance.on('challenge:error', (data) => {
      console.error('Challenge error:', data?.error || JSON.stringify(data));
      setChallengeError({ message: data.error, reason: data.reason || null });
    });

    socketInstance.on('challenge:attempt_finished', (data) => {
      console.log('Challenge attempt finished:', data);
      const message = data.beatOwner
        ? `${data.challengerUsername} pokonał Twoje wyzwanie! Zdobył ${data.fame} Chwały. Możesz zagrać tę partię ponownie i odbić rekord.`
        : `${data.challengerUsername} podjął Twoje wyzwanie i zdobył ${data.fame} Chwały, ale nie zdołał Cię pokonać.`;
      setNotification({ type: 'success', message });
      setTimeout(() => setNotification(null), 8000);
    });

    socketInstance.on('challenge:invite', (data) => {
      console.log('Challenge invite:', data);
      setNotification({
        type: 'info',
        message: `${data.challengerUsername} rzuca Ci wyzwanie! Pokonaj wynik ${data.fame} Chwały w ${data.turns} tur.`
      });
      setTimeout(() => setNotification(null), 8000);
    });

    socketInstance.on('game:city_zone_modal', (data) => {
      setSpectatorCityZoneOpen(data.open);
      setSpectatorCityZoneUser(data.open ? data.username : null);
    });

    socketInstance.on('achievement:unlocked', (data) => {
      // Filter to current user's achievements
      const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      if (userId && data.userId !== userId) return;
      if (!data.titles?.length) return;

      const timestamp = Date.now();
      const newAchievements = data.titles.map((t, i) => ({
        ...t,
        _queueId: `${t.titleId}_${timestamp}_${i}`,
      }));
      setPendingAchievements(prev => [...prev, ...newAchievements]);
      // Refresh user data so profile shows updated unlockedAchievements immediately
      refreshUserRef.current?.();
    });

    socketInstance.on('background:progress', (data) => {
      const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      if (userId && data.userId && data.userId !== userId) return;
      setBackgroundProgress({
        faction: data.faction,
        gameCode: data.gameCode,
        progress: data.progress
      });
    });

    socketInstance.on('background:unlocked', (data) => {
      const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      if (userId && data.userId && data.userId !== userId) return;
      if (!data.backgrounds?.length) return;

      const timestamp = Date.now();
      const newUnlocks = data.backgrounds.map((b, i) => ({
        ...b,
        _queueId: `${b.backgroundId}_${timestamp}_${i}`,
      }));
      setPendingBackgroundUnlocks(prev => [...prev, ...newUnlocks]);
      refreshUserRef.current?.();
    });

    socketInstance.on('avatarFrame:progress', (data) => {
      const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      if (userId && data.userId && data.userId !== userId) return;
      setAvatarFrameProgress({
        gameCode: data.gameCode,
        progress: data.progress
      });
    });

    // Tryb Pojedynek z Legendą — animacje łańcucha combo
    socketInstance.on('combo:chain_updated', (data) => {
      setComboChainEvent({ ...data, _t: Date.now() });
      // Optymistycznie wstaw nowy chain do gameState, zanim dotrze końcowy
      // game:state_update (który czeka na wszystkie passive effecty). Dzięki
      // temu ComboChainDisplay odświeża się natychmiast po kliknięciu karty,
      // a nie z ~200-300ms latencją pełnego broadcastu.
      if (data?.playerId && data.chain) {
        setGameState(prev => {
          if (!prev?.comboRushState) return prev;
          const chains = Array.isArray(prev.comboRushState.comboChains)
            ? prev.comboRushState.comboChains.slice()
            : [];
          const idx = chains.findIndex(c => String(c?.playerId) === String(data.playerId));
          if (idx >= 0) {
            chains[idx] = { ...chains[idx], ...data.chain };
          } else {
            chains.push({ ...data.chain });
          }
          return {
            ...prev,
            comboRushState: { ...prev.comboRushState, comboChains: chains }
          };
        });
      }
      // Toast: pokazuj tylko gdy łańcuch się przedłużył (ignoruj reset/break)
      if (data && !data.broken && data.chainLength > 1) {
        setNotification({
          type: 'info',
          message: `🔥 x${(data.multiplier || 1).toFixed(2)} (${data.chainLength})`,
          ttlMs: 1800
        });
        setTimeout(() => setNotification(null), 1800);
      } else if (data?.broken) {
        setNotification({ type: 'warning', message: '⛓️‍💥 Combo broken', ttlMs: 1800 });
        setTimeout(() => setNotification(null), 1800);
      }
    });

    socketInstance.on('combo:resolved', (data) => {
      setComboResolveEvent({ ...data, _t: Date.now() });
      const parts = [];
      if (data.strengthGain > 0) parts.push(`⚔️ +${data.strengthGain}`);
      if (data.knowledgeGain > 0) parts.push(`📖 +${data.knowledgeGain}`);
      if (data.goldGain > 0) parts.push(`🪙 +${data.goldGain}${data.goldPurchases > 0 ? ` 🛒 +${data.goldPurchases}` : ''}`);
      setNotification({
        type: 'success',
        message: `⚡ ${data.playerName || 'Gracz'} (x${(data.multiplier || 1).toFixed(2)}): ${parts.join('  ')}`,
        ttlMs: 3500
      });
      setTimeout(() => setNotification(null), 3500);
    });

    socketInstance.on('combo:cancelled', (data) => {
      setComboChainEvent({ playerId: data.playerId, cancelled: true, _t: Date.now() });
    });

    socketInstance.on('combo:attack_resolved', (data) => {
      setComboAttackEvent({ ...data, _t: Date.now() });
      const icon = data.resourceKey === 'strength' ? '⚔️' : '📖';
      const targetLabel = data.target === 'boss' ? 'Boss' : 'Epickie Zadanie';
      const msg = data.isDefeated
        ? `🏆 ${data.playerName || 'Gracz'} pokonał ${targetLabel}!`
        : `${icon} ${data.playerName || 'Gracz'} → ${targetLabel}: -${data.damage} HP`;
      setNotification({
        type: data.isDefeated ? 'success' : 'info',
        message: msg,
        ttlMs: 2500
      });
      setTimeout(() => setNotification(null), 2500);
    });

    socketInstance.on('combo:winner', (data) => {
      setComboWinner({ ...data, _t: Date.now() });
    });

    socketInstance.on('combo:heal_milestone', (data) => {
      setComboHealEvent({ ...data, _t: Date.now() });
    });

    socketInstance.on('avatarFrame:unlocked', (data) => {
      const userId = typeof window !== 'undefined' ? localStorage.getItem('userId') : null;
      if (userId && data.userId && data.userId !== userId) return;
      if (!data.frames?.length) return;

      const timestamp = Date.now();
      const newUnlocks = data.frames.map((f, i) => ({
        ...f,
        _queueId: `${f.frameId}_${timestamp}_${i}`,
      }));
      setPendingAvatarFrameUnlocks(prev => [...prev, ...newUnlocks]);
      refreshUserRef.current?.();
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  const createGame = useCallback((playerData) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('game:create', playerData);
  }, [socket]);

  const createTutorialGame = useCallback((playerData) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('game:create', { ...playerData, isTutorial: true, isSingleplayer: true });
  }, [socket]);

  const createGuidedGame = useCallback((playerData) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('game:create', { ...playerData, guidedTutorial: true, isSingleplayer: true });
  }, [socket]);

  // ── System Wyzwań: emitery ──
  const createChallenge = useCallback((gameCode, userId, username) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('challenge:create', { gameCode, userId, username });
  }, [socket]);

  const acceptChallenge = useCallback((code, userId, username) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('challenge:accept', { code, userId, username });
  }, [socket]);

  const inviteToChallenge = useCallback((code, friendIds, userId, username) => {
    if (!socket) return;
    socket.emit('challenge:invite', { code, friendIds, userId, username });
  }, [socket]);

  const clearChallengeError = useCallback(() => {
    setChallengeError(null);
  }, []);

  const clearChallengeShare = useCallback(() => {
    setChallengeShare(null);
  }, []);

  const joinGame = useCallback((gameId, playerData) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('game:join', { gameId, ...playerData });
  }, [socket]);

  const rejoinGame = useCallback((gameCode) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    let userId = localStorage.getItem('userId');
    
    if (!userId) {
      console.warn('[GameContext] rejoinGame: No userId found in localStorage. Generating temporary ID, but rejoin might fail.');
      userId = 'user_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('userId', userId);
    } else {
      console.log(`[GameContext] Rejoining game ${gameCode} as user ${userId}`);
    }
    
    socket.emit('game:rejoin', { gameCode, userId });
  }, [socket]);

  const startHeroSelection = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:hero_selection_phase', { gameCode });
  }, [socket]);

  const startGame = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:start', { gameCode });
  }, [socket]);

  const selectHero = useCallback((gameCode, heroId, faction) => {
    if (!socket) return;
    socket.emit('hero:select', { gameCode, heroId, faction });
  }, [socket]);

  const lastPlayedRef = useRef({ cardId: null, time: 0 });
  const playCard = useCallback((gameId, cardId, iid) => {
    if (!socket) return;
    const now = Date.now();
    if (lastPlayedRef.current.cardId === cardId && now - lastPlayedRef.current.time < 200) {
      console.log('[GameContext] Duplicate play_card blocked:', cardId);
      return;
    }
    lastPlayedRef.current = { cardId, time: now };
    socket.emit('game:play_card', { gameId, cardId, iid });
  }, [socket]);

  const buyCard = useCallback((gameId, cardId, options = {}) => {
    if (!socket) return;
    socket.emit('game:buy_card', { gameId, cardId, paymentMode: options.paymentMode || undefined });
  }, [socket]);

  const challenge = useCallback((gameId, path, cardId, options = {}) => {
    if (!socket) return;
    socket.emit('game:challenge', {
      gameId,
      path,
      cardId,
      // Jedna decyzja o dopłacie (Dar albo Ofiara). mightyBlow/focusedMind zostają dla
      // zgodności ze starszym backendem — serwer traktuje każdą z tych flag tak samo.
      paySurcharge: options.paySurcharge || false,
      mightyBlow: options.mightyBlow || false,
      focusedMind: options.focusedMind || false,
      surchargeKnowledgeKinds: options.surchargeKnowledgeKinds || [],
      paymentMode: options.paymentMode || undefined
    });
  }, [socket]);

  const useCityZone = useCallback((gameId, cardId, choice) => {
    if (!socket) return;
    socket.emit('game:use_city_zone', { gameId, cardId, choice });
  }, [socket]);

  const resolveCombo = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('combo:resolve', { gameCode });
  }, [socket]);

  const cancelCombo = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('combo:cancel', { gameCode });
  }, [socket]);

  const attackComboTarget = useCallback((gameCode, target) => {
    if (!socket) return;
    socket.emit('combo:attack', { gameCode, target });
  }, [socket]);

  const clearComboChainEvent = useCallback(() => setComboChainEvent(null), []);
  const clearComboResolveEvent = useCallback(() => setComboResolveEvent(null), []);
  const clearComboAttackEvent = useCallback(() => setComboAttackEvent(null), []);
  const clearComboWinner = useCallback(() => setComboWinner(null), []);
  const clearComboHealEvent = useCallback(() => setComboHealEvent(null), []);

  const selectGuildContract = useCallback((gameId, contractId) => {
    if (!socket) return;
    socket.emit('game:select_guild_contract', { gameId, contractId });
  }, [socket]);

  const discardGuildContract = useCallback((gameId, contractId) => {
    if (!socket) return;
    socket.emit('game:discard_guild_contract', { gameId, contractId });
  }, [socket]);

  const takeQuest = useCallback((gameId) => {
    if (!socket) return;
    socket.emit('game:take_quest', { gameId });
  }, [socket]);

  const endTurn = useCallback((gameId) => {
    if (!socket) return;
    socket.emit('game:end_turn', { gameId });
  }, [socket]);

  const acknowledgeEvent = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:acknowledge_event', { gameCode });
  }, [socket]);

  const acknowledgeAlarmBreach = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:acknowledge_alarm_breach', { gameCode });
  }, [socket]);

  const leaveGame = useCallback((gameCode) => {
    leftGameCodeRef.current = gameCode || null;
    if (socket && gameCode) socket.emit('game:leave', { gameCode });
    // Reset state locally
    setGameState(null);
    setGameId(null);
    setGameCode(null);
    setLobbyPlayers([]);
    setIsHost(false);
    setHeroSelectionActive(false);
    setGameEndData(null);
    setVictorySplashAllDone(false);
    setVictorySplashAckCount(0);
    setVictorySplashAckTotal(1);
    // Start Wyzwania trzyma kod gry w kontekście — bez wyczyszczenia menu po powrocie
    // od razu przekierowuje z powrotem do /game/<kod> (pętla podsumowania i statystyk).
    setChallengeStartData(null);
  }, [socket]);

  const finishTutorial = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:finish_tutorial', { gameCode });
    // Reset state locally
    setGameState(null);
    setGameId(null);
    setGameCode(null);
    setLobbyPlayers([]);
    setIsHost(false);
    setHeroSelectionActive(false);
  }, [socket]);

  const kickPlayer = useCallback((gameCode, targetUserId) => {
    if (!socket) return;
    socket.emit('game:kick_player', { gameCode, targetUserId });
  }, [socket]);

  const closeLobby = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:close', { gameCode });
    // Reset state locally happens via redirect/disconnect logic, but we can clear eagerly
    setGameState(null);
    setGameId(null);
    setGameCode(null);
    setLobbyPlayers([]);
    setIsHost(false);
    setHeroSelectionActive(false);
  }, [socket]);

  const resolvePassive = useCallback((gameCode, accepted, ability, params = {}) => {
    if (!socket) return;
    // Dołącz bonusEffect jeśli istnieje w passiveAbilityRequest (np. Khela 6+ siły)
    const abilityWithBonus = passiveAbilityRequest?.bonusEffect
      ? { ...ability, bonusEffect: passiveAbilityRequest.bonusEffect }
      : ability;
    // Dołącz triggerContext (potrzebne dla ABSORB_SIN - przekazuje negativeCardId i sourcePlayerId)
    const paramsWithCtx = passiveAbilityRequest?.triggerContext
      ? { ...params, triggerContext: passiveAbilityRequest.triggerContext }
      : params;
    socket.emit('game:resolve_passive', { gameCode, accepted, ability: abilityWithBonus, params: paramsWithCtx });
    setPassiveAbilityRequest(null);
    setHeroAbilityModal(null);
  }, [socket, passiveAbilityRequest]);

  const openActiveAbilityModal = useCallback((heroData) => {
    if (!heroData?.activeAbility) return;
    setHeroAbilityModal({
      mode: 'active',
      ability: heroData.activeAbility,
      hero: heroData
    });
  }, []);

  const closeHeroAbilityModal = useCallback(() => {
    setHeroAbilityModal(null);
  }, []);

  const clearEventEffectsAnimation = useCallback(() => {
    setEventEffectsAnimation(null);
  }, []);

  const setInitialState = useCallback((state) => {
    setGameState(prev => {
      if (prev) return prev;
      if (state.status === 'finished' && state.gameEndData) {
        setGameEndData(state.gameEndData);
      }
      return state;
    });
  }, []);

  const triggerHeroAbility = useCallback((gameCode, params = {}) => {
    if (!socket) return;
    if (params.choice || params.option || params.cardId || params.cardIds) {
      setHeroAbilitySelection(null);
    }
    socket.emit('game:use_hero_ability', { gameCode, params });
  }, [socket]);

  // Wycofanie się z wyboru kart/opcji dla umiejętności bohatera.
  // Backend emituje `hero_ability_needs_selection` PRZED zapisem gry (koszty
  // policzone w HeroService nie są utrwalane bez game.save()), więc anulowanie
  // to czysto lokalne wyczyszczenie stanu — nic nie trzeba cofać po stronie serwera.
  const cancelHeroAbilitySelection = useCallback(() => {
    setHeroAbilitySelection(null);
  }, []);

  const placeSpore = useCallback((gameCode, zone, cardId) => {
    if (!socket) return;
    socket.emit('game:place_spore', { gameCode, zone, cardId });
  }, [socket]);

  const revealShadowCard = useCallback((gameCode, zone, slotIndex) => {
    if (!socket) return;
    socket.emit('game:reveal_shadow_card', { gameCode, zone, slotIndex });
  }, [socket]);

  const activateArtifact = useCallback((gameCode, slotKey) => {
    if (!socket) return;
    socket.emit('game:activate_artifact', { gameCode, slotKey });
  }, [socket]);

  const resolveAutoActive = useCallback((gameCode, accepted, context = {}) => {
    if (!socket) return;
    socket.emit('game:resolve_auto_active', { gameCode, accepted, context });
    setHeroAbilityModal(null);
  }, [socket]);

  const inviteFriend = useCallback((gameCode, friendId) => {
    if (!socket) return;
    socket.emit('game:invite_friend', { gameCode, friendId });
  }, [socket]);

  const quickJoin = useCallback((playerData) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('game:quick_join', playerData);
  }, [socket]);

  const refreshLobbies = useCallback(() => {
    if (!socket) return;
    socket.emit('game:get_open_lobbies');
  }, [socket]);

  const refreshActiveGames = useCallback(() => {
    if (!socket) return;
    socket.emit('game:get_active_games');
  }, [socket]);

  const spectateGame = useCallback((gameCode) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    socket.emit('game:spectate', { gameCode });
  }, [socket]);

  const leaveSpectate = useCallback((gameCode) => {
    if (socket && gameCode) {
      socket.emit('game:leave_spectate', { gameCode });
    }
    setGameState(null);
    setGameCode(null);
    setIsSpectator(false);
    setSpectatorCount(0);
    try { localStorage.removeItem('spectatorGameCode'); } catch {}
  }, [socket]);

  const refreshOnlinePlayers = useCallback(() => {
    if (!socket) return;
    socket.emit('social:get_online_players');
  }, [socket]);

  const checkContractConditions = useCallback((gameCode, contractId) => {
    if (!socket) return;
    setContractCheckResult(null);
    socket.emit('game:check_contract_conditions', { gameCode, contractId });
  }, [socket]);

  const acknowledgeVictorySplash = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:acknowledge_victory_splash', { gameCode });
  }, [socket]);

  const castleRepair = useCallback((gameCode, payload) => {
    if (!socket) return;
    // Wstecznie kompatybilne: jeśli payload to string (cardId), wyślij jako pojedynczy.
    if (typeof payload === 'string') {
      socket.emit('game:castle_repair', { gameCode, cardId: payload });
      return;
    }
    const { handIndices, cardIds } = payload || {};
    socket.emit('game:castle_repair', { gameCode, handIndices, cardIds });
  }, [socket]);

  const castleUpgrade = useCallback((gameCode) => {
    if (!socket) return;
    socket.emit('game:castle_upgrade', { gameCode });
  }, [socket]);

  const finalizeContract = useCallback((gameCode, contractId, chosenCardId = null) => {
    if (!socket) return;
    socket.emit('game:finalize_contract', { gameCode, contractId, chosenCardId });
  }, [socket]);

  const playAgain = useCallback((gameCode) => {
    if (!socket) return;
    leftGameCodeRef.current = null;
    const userId = localStorage.getItem('userId');
    const username = localStorage.getItem('username');
    socket.emit('game:play_again', { gameCode, userId, username });
  }, [socket]);

  const fetchDailyQuests = useCallback(() => {
    if (!socket) return;
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    socket.emit('quest:get_daily', { userId });
  }, [socket]);

  const claimQuestReward = useCallback((questId) => {
    if (!socket) return;
    const userId = localStorage.getItem('userId');
    if (!userId) return;
    socket.emit('quest:claim_reward', { userId, questId });
  }, [socket]);

  const sendChatMessage = useCallback((gameCode, message) => {
    if (!socket) return;
    const username = localStorage.getItem('username') || 'Gracz';
    const activeTitle = localStorage.getItem('activeTitle');
    socket.emit('game:chat_message', { gameCode, message, username, activeTitle });
  }, [socket]);

  const emitCityZoneModal = useCallback((gameCodeArg, open) => {
    if (!socket) return;
    const username = localStorage.getItem('username') || 'Gracz';
    socket.emit('game:city_zone_modal', { gameCode: gameCodeArg, open, username });
  }, [socket]);

  const emitCursorMove = useCallback((gameCodeArg, x, y, hoveredCardId = null, anchors = null) => {
    if (!socket) return;
    const username = localStorage.getItem('username') || 'Gracz';
    const userId = localStorage.getItem('userId');
    const activeTitle = localStorage.getItem('activeTitle');
    socket.emit('game:cursor_move', {
      gameCode: gameCodeArg, x, y, username, userId, activeTitle, hoveredCardId,
      ...(anchors || {}),
    });
  }, [socket]);

  const emitUiAction = useCallback((gameCodeArg, modal, open) => {
    if (!socket) return;
    socket.emit('game:ui_action', { gameCode: gameCodeArg, modal, open });
  }, [socket]);

  const value = {
    socket,
    isConnected,
    chatMessages,
    sendChatMessage,
    spectatorCityZoneOpen,
    spectatorCityZoneUser,
    emitCityZoneModal,
    emitCursorMove,
    emitUiAction,
    gameState,
    gameId,
    gameCode,
    lobbyPlayers,
    isHost,
    error,
    heroSelectionActive,
    pendingEvent,
    alarmBreach,
    castleMarchAnimating,
    passiveAbilityRequest,
    heroAbilitySelection,
    cancelHeroAbilitySelection,
    heroAbilityModal,
    gameEndData,
    eventEffectsAnimation,
    onlineCount,
    onlinePlayers,
    openLobbies,
    activeGames,
    isSpectator,
    spectatorCount,
    notification,
    setNotification, // Expose setter
    sinAbsorbedEvent,
    setSinAbsorbedEvent,
    dailyQuests,
    isLobbyViewActive,
    setIsLobbyViewActive,
    lobbyViewRequest,
    requestLobbyView,
    createGame,
    createTutorialGame,
    createGuidedGame,
    joinGame,
    quickJoin,
    refreshLobbies,
    refreshActiveGames,
    spectateGame,
    leaveSpectate,
    refreshOnlinePlayers,
    rejoinGame,
    leaveGame,
    finishTutorial,
    closeLobby,
    kickPlayer,
    inviteFriend,
    checkContractConditions,
    finalizeContract,
    playAgain,
    fetchDailyQuests,
    claimQuestReward,
    contractCheckResult,
    setContractCheckResult,
    fulfilledContractIds,
    acknowledgeVictorySplash,
    castleRepair,
    castleUpgrade,
    victorySplashAckCount,
    victorySplashAckTotal,
    victorySplashAllDone,
    startHeroSelection,
    startGame,
    selectHero,
    playCard,
    buyCard,
    challenge,
    useCityZone,
    selectGuildContract,
    discardGuildContract,
    takeQuest,
    endTurn,
    acknowledgeEvent,
    acknowledgeAlarmBreach,
    resolvePassive,
    triggerHeroAbility,
    placeSpore,
    activateArtifact,
    openActiveAbilityModal,
    closeHeroAbilityModal,
    resolveAutoActive,
    clearEventEffectsAnimation,
    setInitialState,
    revealShadowCard,
    pendingAchievements,
    dismissAchievement,
    hasUnseenAchievements,
    unseenAchievementsCount,
    backgroundProgress,
    clearBackgroundProgress,
    pendingBackgroundUnlocks,
    dismissBackgroundUnlock,
    avatarFrameProgress,
    clearAvatarFrameProgress,
    pendingAvatarFrameUnlocks,
    dismissAvatarFrameUnlock,
    // Tryb Pojedynek z Legendą
    resolveCombo,
    cancelCombo,
    attackComboTarget,
    comboChainEvent,
    comboResolveEvent,
    comboAttackEvent,
    comboWinner,
    comboHealEvent,
    clearComboChainEvent,
    clearComboResolveEvent,
    clearComboAttackEvent,
    clearComboWinner,
    clearComboHealEvent,
    // System Wyzwań
    challengeShare,
    challengeStartData,
    clearChallengeStartData: () => setChallengeStartData(null),
    challengeError,
    challengeInvitesSent,
    createChallenge,
    acceptChallenge,
    inviteToChallenge,
    clearChallengeError,
    clearChallengeShare,
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
}

export const useGameContext = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGameContext must be used within a GameProvider');
  }
  return context;
};
